// OrderItemBase

export type OrderItemType = "OnDemandRental" | "SubscriptionRental" | "ExRentalSale" | "NonRentalSale";

export interface OrderItemBase {
  is_rental: boolean;
  designer: string;
  name: string;
  url: string;
  image_url: string;
  size?: string | null;
  label_size?: string | null;
  label_size_country?: string | null;
  product_code: string;
  unit_code?: string | null;
  quantity: number;
  price_paid_in_cents: number;
  distributed_amount_in_cents: number;
  discount_in_cents: number;
  is_garment?: boolean;
  start_date?: string | null;
  end_date?: string | null;
  formatted_start_date?: string | null;
  formatted_end_date?: string | null;
  order_item_type: OrderItemType;
}

// SaleOrderItem

export type OrderItemTypeSale = "ExRentalSale" | "NonRentalSale";

export interface SaleOrderItem extends OrderItemBase {
  designer: string;
  name: string;
  url: string;
  image_url: string;
  size?: string | null;
  label_size?: string | null;
  label_size_country?: string | null;
  product_code: string;
  unit_code?: string | null;
  quantity: number;
  price_paid_in_cents: number;
  distributed_amount_in_cents: number;
  discount_in_cents: number;
  is_garment?: boolean;
  start_date?: null;
  end_date?: null;
  is_rental: false;
  formatted_start_date?: null;
  formatted_end_date?: null;
  order_item_type: OrderItemTypeSale;
}

// RentalOrderItem

export type OrderItemTypeRental = "OnDemandRental" | "SubscriptionRental";

export interface RentalOrderItem extends OrderItemBase {
  designer: string;
  name: string;
  url: string;
  image_url: string;
  size?: string | null;
  label_size?: string | null;
  label_size_country?: string | null;
  product_code: string;
  unit_code?: string;
  quantity: number;
  price_paid_in_cents: number;
  distributed_amount_in_cents: number;
  discount_in_cents: number;
  is_garment?: boolean;
  start_date: string | null;
  end_date: string | null;
  is_rental: true;
  formatted_start_date?: string | null;
  formatted_end_date?: string | null;
  order_item_type: OrderItemTypeRental;
}

// SubscriptionPlan

export interface SubscriptionPlan {
  id: string;
  legacy: boolean;
  name: string;
  price: string;
}

// DateRange

export interface DateRange {
  startDate: string;
  endDate: string;
}

// CustomerSubscriptionStatus

export type CustomerSubscriptionStatus = "Active" | "Canceled" | "PastDue" | "Pending" | "Expired" | "Paused";

// CustomerSubscription

export interface CustomerSubscription {
  glamcornerCustomerId: string;
  braintreeCustomerId: string;
  subscriptionId: string;
  nextBillingDate: string;
  pastDueDate: string | null;
  email?: string;
  pauseDates: DateRange[];
  plan: SubscriptionPlan;
  status: CustomerSubscriptionStatus;
  transactions: {
    id: string;
    status: string;
    description: string;
    type: string;
    amount: string;
    last4?: string;
    cardType?: string;
    billingPeriodEndDate: string | null;
    billingPeriodStartDate: string | null;
    createdAt: string;
    plan: null | SubscriptionPlan;
  }[];
}

// OrderItem

export type OrderItem = SaleOrderItem | RentalOrderItem;

// Address

export interface Address {
  first_name?: string;
  last_name?: string;
  street: string[];
  suburb: string;
  state: string;
  post_code: string;
  country_code: string;
  company?: string | null;
  email?: string;
  phone?: string;
}

// SubOrder

export interface SubOrder {
  sub_order_id: string;
  order_items: OrderItem[];
  non_cancelled_order_items: OrderItem[];
  cancelled_order_items: OrderItem[];
  effective_delivery_cost_in_cents: number;
  total_paid_with_store_credit_in_cents: number;
  total_paid_with_cash_in_cents: number;
  total_discount_in_cents: number;
  is_cancelled: boolean;
  last_date_refund_allowed: string | null;
  send_date: string | null;
  expected_return_date: string | null;
  status: SubOrderStatus;
  shipping_address: Address;
}

export const enum SubOrderStatus {
  Cancelled = "cancelled",
  Dispatched = "dispatched",
  Packing = "packing",
  RefundRequested = "refund_requested",
  Refunded = "refunded",
  Reserved = "reserved",
  Unknown = "unknown"
}

// Order

export interface Order {
  order_id: string;
  customer_first_name: string;
  customer_last_name: string;
  email: string;
  ordered_at: string;
  sydney_formatted_order_time: string;
  subtotal: number;
  shipping: number;
  tax: number;
  grand_total: number;
  billing_address: Address;
  shipping_address: Address;
  authority_to_leave: boolean;
  delivery_instructions?: string | null;
  customer_note?: string | null;
  shipping_method?: string | null;
  items: OrderItem[];
}

// ImageInfoGalleryItem

export interface ImageInfoGalleryItem {
  visibility_rules: string | null;
  url_by_pixel_density?: {
    x1: string;
    x1_5: string;
    x2: string;
    x3: string;
    [k: string]: any;
  };
  url_by_size_and_pixel_density: {
    large: {
      x1: string;
      x1_5: string;
      x2: string;
      x3: string;
    };
    small: {
      x1: string;
      x1_5: string;
      x2: string;
      x3: string;
    };
  };
}

// ImageInfo

export interface ImageInfo {
  label: string | null;
  gallery: ImageInfoGalleryItem[];
  url: string;
}

// Style

export interface Style {
  buy_price_in_cents: number | null;
  discount_buy_price_in_cents: number | null;
  designer: string;
  image_info: ImageInfo;
  magento_product_id: number;
  name: string;
  record_type: StyleType;
  rental_prices:
    | null
    | {
        rental_days: number;
        discount_rental_price_in_cents: number | null;
        rental_price_in_cents: number;
      }[];
  retail_price_in_cents: number;
  /**
   * @deprecated
   */
  price_in_cents: number | null;
  /**
   * @deprecated
   */
  discount_price_in_cents?: number | null;
  print_name: string | null;
  try_on_allowed: boolean;
  is_backupable: boolean;
  style_code: string;
  url: string;
  designations_include_subscription: boolean;
  designations_include_rental: boolean;
  designations_include_sale: boolean;
  tiers: string[];
}

export const enum StyleType {
  RentalUnit = "rental_unit",
  SaleProduct = "sale_product"
}

// ReturnUnit

export interface ReturnUnit {
  returned_booking: ReturnBooking;
  next_booking: null | ReturnBooking;
  unit: {
    unit_code: string;
    designer: string;
    image_url: string;
    name: string;
    product_code: string;
    size: string;
    label_size: string | null;
    label_size_country: string | null;
    style_code: string;
    pieces: string[];
    [k: string]: any;
  };
  unit_needed_today: boolean;
}

// ReturnBooking

export interface ReturnBooking {
  unit_location_id: number;
  completed_at: string | null;
  worn: boolean;
  needs_cleaning: boolean;
  damaged: boolean;
  customer: string;
  sub_order_id: string;
  send_date: string | null;
  start_date: string | null;
  end_date: string | null;
  expected_return_date: string | null;
  booking_type: string;
}

// Address2

export interface Address2 {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string | null;
  suburb: string;
  state: string;
  postCode: string;
  countryCode: string;
  company: string | null;
  email: string;
  phone: string;
  authToLeave?: boolean;
}

// OrderPlacedMessageOrderItem

export interface OrderPlacedMessageOrderItem {
  bookingType?: "Subscription" | "rental" | "sale";
  startDate: string | null;
  endDate: string | null;
  formattedStartDate: string | null;
  formattedEndDate: string | null;
  designer: string;
  name: string;
  productCode: string;
  size: string | null;
  labelSize: string | null;
  labelSizeCountry: string | null;
  distributedAmountInCents: number;
  pricePaidInCents: number;
  priceInCents: number;
  discountInCents: number;
  quantity: number;
  imageUrl: string;
  url: string;
  isRental: boolean;
  isGarment: boolean;
  itemId?: string;
}

// OrderPlacedMessageOrder

export interface OrderPlacedMessageOrder {
  orderId: string;
  orderType: "on_demand" | "subscription";
  backupPairs?: {
    [k: string]: any;
  };
  customerFirstName: string;
  customerLastName: string;
  customerId?: string | null;
  customerTiers?: string[];
  email: string;
  orderedAt: string;
  sydneyFormattedOrderTime: string;
  subtotal: number;
  paidWithStoreCredit: number;
  paidWithCash: number;
  discount: number;
  shipping: number;
  shippingDiscount: number;
  tax: number;
  grandTotal: number;
  billingAddress: Address2 | null;
  shippingAddress: Address2;
  authorityToLeave: boolean;
  deliveryInstructions?: string | null;
  customerNote?: string | null;
  shippingMethod: string | null;
  items: OrderPlacedMessageOrderItem[];
  storefront?: string | null;
  attributes?: {
    [k: string]: any;
  };
}

// OrderPlacedMessage2

export interface OrderPlacedMessage2 {
  type: "OrderPlaced";
  version: 2;
  order: OrderPlacedMessageOrder;
}

// SubscriptionPlan

export interface SubscriptionPlan {
  id: string;
  legacy: boolean;
  name: string;
  price: string;
}

// GcSessionInfo

export interface GcSessionInfo {
  token: string;
  formKey: string;
  justLoggedIn: boolean;
  loggedIn: boolean;
  featureFlags: string[];
  customerInfo?: {
    customerGroup: string;
    customerId: string;
    email: string;
    firstName: string;
    lastName: string;
    shippingAddress?: {
      [k: string]: any;
    } | null;
    [k: string]: any;
  };
  postcode?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  traceToken: string;
}

// MetricEvent

export interface MetricEvent {
  type: "MetricEvent";
  name: string;
  tags?: {
    [k: string]: any;
  };
  metadata?: {
    [k: string]: any;
  };
  value: number;
  metricType: "gauge" | "counter" | "timer";
}

// CartItem

export type ProductType = "Rental" | "Sale";

export interface CartItem {
  designer: string;
  id: string;
  productCode: string;
  cartItemType: ProductType;
  name: string;
  priceInCents: number | null;
  salePriceInCents: number | null;
  url: string;
  styleCode: string;
  size: string;
  imageUrl: string;
  isBackupable: boolean;
  isBackup: boolean;
  isClearanceItem: boolean;
}

// CartSubOrder

export interface CartSubOrder {
  startDate: string | null;
  endDate: string | null;
  daysCount: number;
  postcode: string | null;
  items: CartItem[];
}

// PaymentSummaryItem

export interface PaymentSummaryItem {
  name: "Delivery & Return" | "Discounts" | "Cancellation Protection" | "Grand Total" | "Store Credits" | "Subtotal";
  value: number;
}

// AddOn

export interface AddOn {
  active: boolean;
  code: string;
  priceInCents: number;
}

// Cart

export interface Cart {
  addOns: AddOn[];
  quoteId: string;
  bagKey: string;
  couponCode: string;
  subOrders: CartSubOrder[];
  paymentSummary: PaymentSummaryItem[];
}

// AddCartItemPayload

export interface AddCartItemPayload {
  buyout: boolean;
  productCode: string;
  productType: ProductType;
  postcode: string;
  size: string | null;
  startDate: string | null;
  endDate: string | null;
  inBackupMode?: boolean | null;
}

// UpdateCartItemPayload

export interface UpdateCartItemPayload {
  productCode: string;
  productType: ProductType;
  itemId: string;
  postcode: string;
  startDate: string | null;
  endDate: string | null;
}

// SetAddOnStatePayload

export interface SetAddOnStatePayload {
  active: boolean;
  code: string;
}

// WishlistItem

export interface WishlistItem {
  styleCode: string;
}

// Favourite

export interface Favourite {
  styleCode: string;
}

// GcAddress

export interface GcAddress {
  company: string | null;
  countryCode: string;
  firstName: string;
  lastName: string;
  phone: string;
  postCode: string;
  state: string;
  addressLine1: string;
  addressLine2: string | null;
  suburb: string;
  email: string;
  authToLeave: boolean;
}

// Profile

export interface Profile {
  customerId: string;
  customerGroup: string;
  email: string;
  firstName: string;
  lastName: string;
  shippingAddress?: GcAddress;
  stylingPreferences?: {
    size?: string[];
  };
}

// CustomerSubscriptionProfile

export interface CustomerSubscriptionProfile {
  active: boolean;
  boxesPerMonth: number | "unlimited";
  customerId: string;
  email: string;
  glamcornerCustomerId: string;
  maxItems: number;
  nextBillingDate: string;
  plan: SubscriptionPlan;
  reservedItemsFeatureFlag: boolean;
  status: CustomerSubscriptionStatus;
  tiersAccessible: string[];
}

// ProfileWithSubscription

export type ProfileWithSubscription = Profile & {
  customerSubscription: CustomerSubscriptionProfile;
};

// AvailabilityFilterState

export interface AvailabilityFilterStateDelete {
  state: AvailabilityFilterState;
}

export const enum AvailabilityFilterState {
  Any = "any",
  NextBox = "next-box",
  Now = "now"
}
